import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Button } from '@mui/material';
import { FormContainer, useForm } from 'react-hook-form-mui';
import { useLocalStorageState } from 'ahooks';
import { format } from 'date-fns';
import { selectSelectedBilledEntities } from 'src/infra/stores/reducer/billedEntitiesSlice';
import { useAddCredit } from 'src/use-cases/settings/useCreditTransactions';
import useToast from 'src/use-cases/toast/useToast';
import { useUserContext } from 'src/use-cases/user/userDetails';
import { SettingsMain } from '../SettingsMain';
import { CreditMetrics } from './CreditMetrics';
import { CreditSummaryCards } from './CreditSummaryCards';
import { CreditTransactionsHeader } from './CreditTransactionsHeader';
import { AddCreditsModal } from './AddCreditsModal';
import { StartBillingCycleModal } from './StartBillingCycleModal';
import { CreditTransactionsTable } from './CreditTransactionsTable';

// Form values interface for the dropdowns and table integration
interface CreditTransactionsFormValues {
  billedEntityId: string;
  transactionDate: string[] | null;
}

// Storage key for persisting only billed entity ID
const STORAGE_KEY = 'credit-transactions-billed-entity';

/**
 * Credit Transactions main component that displays credit metrics and transaction history
 */
export const CreditTransactionsMain: React.FC = () => {
  // Get selected billed entities from Redux store
  const selectedEntities = useSelector(selectSelectedBilledEntities);

  const { userDetails } = useUserContext();
  const userRoles = userDetails?.roles ?? [];
  const isCreditViewerRole = userRoles.includes('CREDIT_VIEWER');

  // Get the first selected billed entity (assuming single entity selection for credits)
  const defaultSelectedEntity = useMemo(() => {
    if (selectedEntities.length > 0) {
      return selectedEntities[0];
    }
    return null;
  }, [selectedEntities]);

  // Persistent storage for billed entity ID only
  const [storedBilledEntityId, setStoredBilledEntityId] = useLocalStorageState<string>(STORAGE_KEY, {
    defaultValue: defaultSelectedEntity?.id?.toString() || '',
  });

  // Form setup - no default transaction date, let TransactionDateMenu handle it
  const defaultValues: CreditTransactionsFormValues = useMemo(
    () => ({
      billedEntityId: storedBilledEntityId || defaultSelectedEntity?.id?.toString() || '',
      transactionDate: null, // Null until TransactionDateMenu initializes it
    }),
    [storedBilledEntityId, defaultSelectedEntity?.id]
  );

  const formContext = useForm({
    defaultValues,
  });

  // Watch form values to get current selections
  const formValues = formContext.watch();

  // Persist only billed entity ID when it changes
  useEffect(() => {
    if (formValues.billedEntityId !== undefined && formValues.billedEntityId !== storedBilledEntityId) {
      setStoredBilledEntityId(formValues.billedEntityId);
    }
  }, [formValues.billedEntityId, storedBilledEntityId, setStoredBilledEntityId]);

  // Update form billed entity when default entity changes (but preserve user selections)
  useEffect(() => {
    if (defaultSelectedEntity?.id && !formValues.billedEntityId) {
      formContext.setValue('billedEntityId', defaultSelectedEntity.id.toString());
    }
  }, [defaultSelectedEntity?.id, formValues.billedEntityId, formContext]);

  // State for Add Credits modal
  const [isAddCreditsModalOpen, setIsAddCreditsModalOpen] = useState(false);

  // State for Start Billing Cycle modal
  const [isBillingCycleModalOpen, setIsBillingCycleModalOpen] = useState(false);
  // Add Credits mutation hook
  const { mutate: addCredit, isLoading: isAddingCredit } = useAddCredit();
  const openToast = useToast();

  // Handler for Add Credits button
  const handleAddCredits = () => {
    setIsAddCreditsModalOpen(true);
  };

  // Handler for Add Credits modal submit
  const handleAddCreditsSubmit = (values: any) => {
    // Transform form values to API format
    const apiPayload = {
      billedEntityId: Number(values.billedEntityId),
      invoiceAmount: values.invoiceCredits ? Number(values.invoiceCredits) : undefined,
      invoiceExpirationDate: values.invoiceCreditExpiryDate ? format(values.invoiceCreditExpiryDate, 'yyyy-MM-dd') : undefined,
      shipmentAmount: values.shipmentCredits ? Number(values.shipmentCredits) : undefined,
      shipmentExpirationDate: values.shipmentCreditExpiryDate ? format(values.shipmentCreditExpiryDate, 'yyyy-MM-dd') : undefined,
      poNumber: values.poNumber,
      poDate: values.poDate ? format(values.poDate, 'yyyy-MM-dd') : undefined,
    };

    addCredit(apiPayload, {
      onSuccess: () => {
        openToast({
          toastType: 'success',
          title: 'Success',
          description: 'Credits added successfully',
        });
        setIsAddCreditsModalOpen(false);
      },
      onError: (error: any) => {
        openToast({
          toastType: 'error',
          title: 'Error',
          description: error?.response?.data?.message || 'Failed to add credits. Please try again.',
        });
      },
    });
  };

  // Handler for Add Credits modal close
  const handleAddCreditsClose = () => {
    setIsAddCreditsModalOpen(false);
  };

  return (
    <FormContainer formContext={formContext}>
      <SettingsMain
        title='Credit Transaction History'
        subtitle='See all your credit transactions here'
        backPath='/settings'
        topActions={
          <div className='flex items-center gap-16'>
            <CreditTransactionsHeader />
            {!isCreditViewerRole && userDetails?.systemAdmin && (
              <Button
                variant='contained'
                color='success'
                onClick={handleAddCredits}
                sx={{
                  backgroundColor: '#00992B',
                  '&:hover': {
                    backgroundColor: '#007A22',
                  },
                  textTransform: 'none',
                  fontWeight: 500,
                }}
              >
                Add Credits
              </Button>
            )}
            {/* Start Billing Cycle button — temporarily disabled. Re-enable when ready to use:
            {!userDetails?.systemAdmin && (
              <Button
                variant='contained'
                color='success'
                onClick={() => setIsBillingCycleModalOpen(true)}
                sx={{
                  backgroundColor: '#00992B',
                  '&:hover': {
                    backgroundColor: '#007A22',
                  },
                  textTransform: 'none',
                  fontWeight: 500,
                }}
              >
                Start Billing Cycle
              </Button>
            )}
            */}
          </div>
        }
      >
        {/* Summary Cards — temporarily disabled. Re-enable when ready to use:
        {!userDetails?.systemAdmin && (
          <div className='px-16 xl:px-24 pt-20 pb-20 bg-neutral-10'>
            <CreditSummaryCards />
          </div>
        )}
        */}

        {/* Credit Metrics Section */}
        <div className='px-16 xl:px-24 py-24 bg-neutral-10'>
          <CreditMetrics />
        </div>

        {/* Credit Transactions Table */}
        <CreditTransactionsTable />
      </SettingsMain>

      {/* Add Credits Modal */}
      <AddCreditsModal open={isAddCreditsModalOpen} onClose={handleAddCreditsClose} onSubmit={handleAddCreditsSubmit} isLoading={isAddingCredit} />
      {/* Start Billing Cycle Modal */}
      <StartBillingCycleModal
        open={isBillingCycleModalOpen}
        onClose={() => setIsBillingCycleModalOpen(false)}
        onSubmit={(values) => {
          console.log('Start Billing Cycle values:', values);
          setIsBillingCycleModalOpen(false);
        }}
      />
    </FormContainer>
  );
};
